import React, { lazy, useEffect } from "react";

// Libs
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { QueryParamProvider, StringParam, useQueryParams } from "use-query-params";
import Intercom from "@intercom/messenger-js-sdk";

// Midlewares
import RequireAuth from "routes/requirements/RequireAuth";
import RequireShop from "routes/requirements/RequireShop";
import RequireNonAmazonAccount from "routes/requirements/RequireNonAmazonAccount";
import RequireNotBlockedRouteChange from "routes/requirements/RequireNotBlockedRouteChange";

// Routes
import routes from "./routes";
import redirects from "./redirects";

// Utils
import { isSpocketUser } from "utils/email";
import { getIntercomToken } from "utils/api/user/requests";

// Components
import { NotFoundPage } from "pages/NotFound";
import ErrorPage from "pages/ErrorPage";
const Navigation = lazy(() => import("components/Navigation"));

let intercomBootedForUserId = null;

const bootIntercomForUser = async (userId, email) => {
  const appId = process.env.REACT_APP_INTERCOM_KEY;
  if (!appId) return;
  if (intercomBootedForUserId === userId) return;
  intercomBootedForUserId = userId;

  let intercom_user_jwt;
  try {
    ({ intercom_user_jwt } = await getIntercomToken());
  } catch (error) {
    intercomBootedForUserId = null;
    console.error("Failed to fetch Intercom token", error);
    return;
  }

  Intercom({
    app_id: appId,
    user_id: userId,
    email,
    intercom_user_jwt
  });
};

const navWrapper = Component => props => {
  const [query] = useQueryParams({
    pricing: StringParam,
    show_rating: StringParam
  });

  return (
    <Navigation {...props} pricing={query.pricing} showRating={query.show_rating}>
      <Component />
    </Navigation>
  );
};

const WrapperUseQueryParams = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    const authToken = localStorage.getItem("auth_token");
    const dropshipperId = localStorage.getItem("dropshipper_id");
    const email = localStorage.getItem("user_email");
    if (!authToken || !dropshipperId) return;
    bootIntercomForUser(dropshipperId, email);
  }, []);

  return (
    <QueryParamProvider
      history={{ replace: navigate, push: navigate }}
      location={location}
      adapter={ReactRouter6Adapter}
    >
      {children}
    </QueryParamProvider>
  );
};

export const makeRedirects = () => {
  return redirects.map(({ from, to }) => ({
    path: from,
    element: <Navigate to={to} replace />
  }));
};

export const makeRoutes = () => {
  const isSpocketUserVal = isSpocketUser();

  return [
    ...routes
      .map(({ path, Component, app, shop, blockedForAmazonUsers, spocketUser }) => {
        if (app) {
          if (isSpocketUserVal) {
            Component = RequireAuth(navWrapper(Component));
          } else {
            // Sometimes users might be blocked on Search page so we should not allow them to go to other routes
            Component = RequireAuth(RequireNotBlockedRouteChange(navWrapper(Component)));
          }
        }

        if (shop) {
          Component = RequireShop(Component);
        }

        if (blockedForAmazonUsers) {
          Component = RequireNonAmazonAccount(Component);
        }

        // If route is set to be used by Spocket user and user is not Spocket, return null
        if (spocketUser !== undefined && spocketUser !== isSpocketUserVal) return null;

        return {
          path,
          element: (
            <WrapperUseQueryParams>
              <Component />
            </WrapperUseQueryParams>
          ),
          errorElement: <ErrorPage />
        };
      })
      .filter(t => t),
    { errorElement: <NotFoundPage /> }
  ];
};
